import { Compass, RotateCw } from 'lucide-react';
import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, useLocation, useParams, useRouteError } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { EmptyState, Spinner } from '@/components/ui/misc';
import { ResourcePage } from '@/engine/resource-page';
import { RESOURCES } from '@/engine/resources';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { session } from '@/lib/session';
import { LoginPage } from '@/features/auth/login-page';
import { StablePicker } from '@/features/auth/stable-picker';
import { AppShell } from './shell';

// Feature screens are code-split; each file under features/ exports its page component by name.
const PAGES = import.meta.glob<Record<string, ComponentType>>('../features/**/*-page.tsx');

function page(file: string, name: string) {
  const load = PAGES[`../features/${file}.tsx`];
  if (!load) return <NotFound />;
  const C = lazy(() => load().then((m) => ({ default: m[name] })));
  return (
    <Suspense fallback={<PageLoader />}>
      <C />
    </Suspense>
  );
}

export function PageLoader() {
  return (
    <div className="grid min-h-[50vh] place-items-center">
      <Spinner className="size-6 text-primary" />
    </div>
  );
}

function FullScreenLoader() {
  return (
    <div className="grid min-h-screen place-items-center">
      <div className="flex flex-col items-center gap-4">
        <img src="/favicon.svg" className="size-12 animate-pulse" alt="" />
        <Spinner className="size-5 text-muted-foreground" />
      </div>
    </div>
  );
}

/** Signed in + a stable selected (or a platform route for platform admins). */
function RequireStable({ platform }: { platform?: boolean }) {
  const { signedIn, tenant, me, meError, retryMe, isPlatformAdmin } = useAuth();
  const location = useLocation();
  const lostAccess = meError instanceof ApiError && [403, 404].includes(meError.status);

  useEffect(() => {
    // Membership revoked or the stable is gone: go back to the picker.
    if (lostAccess) session.set({ tenant: null });
  }, [lostAccess]);

  if (!signedIn) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (platform) {
    if (!isPlatformAdmin) return <Navigate to="/" replace />;
    return <AppShell />;
  }
  if (!tenant) return <Navigate to="/stables" replace />;
  if (!me && meError && !lostAccess)
    return (
      <EmptyState
        className="mt-32"
        title="Can’t reach the server"
        description={meError instanceof Error ? meError.message : 'Check your connection and try again.'}
        action={<Button variant="outline" onClick={retryMe}><RotateCw /> Try again</Button>}
      />
    );
  if (!me) return <FullScreenLoader />;
  return <AppShell />;
}

function PublicOnly({ children }: { children: ReactNode }) {
  const { signedIn, tenant } = useAuth();
  const location = useLocation();
  if (signedIn) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={tenant ? from ?? '/' : '/stables'} replace />;
  }
  return <>{children}</>;
}

function SignedIn({ children }: { children: ReactNode }) {
  const { signedIn, tenant } = useAuth();
  if (!signedIn) return <Navigate to="/login" replace />;
  if (tenant) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function GenericResource() {
  const { key = '' } = useParams();
  const config = RESOURCES[key];
  useEffect(() => {
    if (config) document.title = `${config.title} · Stable Admin`;
  }, [config]);
  if (!config) return <NotFound />;
  return <ResourcePage key={key} config={config} />;
}

export function NotFound() {
  return (
    <EmptyState
      className="mt-24"
      icon={<Compass className="size-6" />}
      title="Page not found"
      description="This page doesn’t exist or was moved."
      action={
        <Button variant="outline" onClick={() => history.back()}>
          Go back
        </Button>
      }
    />
  );
}

function RouteError() {
  const error = useRouteError();
  const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Unexpected error';
  // A new deployment replaced lazily loaded chunks: reload once to pick them up.
  if (/dynamically imported module|Importing a module script failed/i.test(message) && !sessionStorage.getItem('stable.reloaded')) {
    sessionStorage.setItem('stable.reloaded', '1');
    location.reload();
  }
  return (
    <EmptyState
      className="mt-24"
      title="Something went wrong"
      description={message}
      action={
        <Button variant="outline" onClick={() => location.reload()}>
          <RotateCw /> Reload
        </Button>
      }
    />
  );
}

export const BESPOKE = [
  { path: 'schedule', element: page('schedule/schedule-page', 'SchedulePage') },
  { path: 'check-in', element: page('schedule/check-in-page', 'CheckInPage') },
  { path: 'inbox', element: page('inbox/inbox-page', 'InboxPage') },
  { path: 'tasks', element: page('yard/tasks-page', 'TasksPage') },
  { path: 'stalls', element: page('yard/stalls-page', 'StallsPage') },
  { path: 'people/:id', element: page('people/person-page', 'PersonPage') },
  { path: 'horses/:id', element: page('horses/horse-page', 'HorsePage') },
  { path: 'health', element: page('horses/health-page', 'HealthPage') },
  { path: 'boarding', element: page('boarding/boarding-page', 'BoardingPage') },
  { path: 'analytics', element: page('analytics/analytics-page', 'AnalyticsPage') },
  { path: 'website', element: page('website/website-page', 'WebsitePage') },
  { path: 'settings', element: page('settings/settings-page', 'SettingsPage') },
  { path: 'team', element: page('settings/team-page', 'TeamPage') },
  { path: 'explorer', element: page('explorer/explorer-page', 'ExplorerPage') },
];

export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      { path: '/login', element: <PublicOnly><LoginPage /></PublicOnly> },
      { path: '/stables', element: <SignedIn><StablePicker /></SignedIn> },
      {
        path: '/platform',
        element: <RequireStable platform />,
        children: [
          { index: true, element: page('platform/platform-page', 'PlatformTenantsPage') },
          { path: 'flags', element: page('platform/platform-page', 'PlatformFlagsPage') },
          { path: 'ops', element: page('platform/platform-page', 'PlatformOpsPage') },
        ],
      },
      {
        path: '/',
        element: <RequireStable />,
        children: [
          { index: true, element: page('dashboard/dashboard-page', 'DashboardPage') },
          ...BESPOKE,
          { path: ':key', element: <GenericResource /> },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);
